/**
 * Lemai Face — Facial Recognition & People Intelligence Module
 *
 * Four tabs:
 *   1. Watchlist Alerts  — live-polling alert cards (mock, every 5s)
 *   2. People Counting   — zone occupancy + crowd flow chart + SVG heatmap
 *   3. Access Control    — face-based entry/exit event log
 *   4. Forensic Search   — photo upload → mock match results + person timeline
 *
 * CORSIGHT INTEGRATION POINTS:
 *   All live API calls are marked with:
 *     // CORSIGHT API: <METHOD> <endpoint> — replace mock data here
 *   Keep these isolated so they can be swapped in after the credentials meeting.
 *
 * Author: Lemai Engineering
 * Last updated: 2026-04-12
 */

import React, {
  useState, useEffect, useRef, useCallback, useMemo,
} from 'react';
import Layout from '../../components/Layout';
import { useLicense } from '../../context/LicenseContext';

// ─── Colour tokens (match existing Lemai dark theme) ─────────────────────────

const C = {
  bg:        '#0a0f1e',
  surface:   'rgba(255,255,255,0.03)',
  border:    'rgba(255,255,255,0.07)',
  borderHi:  'rgba(255,255,255,0.13)',
  text:      '#f1f5f9',
  textMid:   '#94a3b8',
  textDim:   '#555555',
  textFaint: '#222222',
  blue:      '#3b82f6',
  blueDim:   'rgba(59,130,246,0.15)',
  green:     '#22c55e',
  greenDim:  'rgba(34,197,94,0.12)',
  amber:     '#f59e0b',
  amberDim:  'rgba(245,158,11,0.12)',
  red:       '#ef4444',
  redDim:    'rgba(239,68,68,0.12)',
  purple:    '#a855f7',
  purpleDim: 'rgba(168,85,247,0.12)',
  cyan:      '#06b6d4',
};

// ─── Shared helpers ───────────────────────────────────────────────────────────

function timeAgo(ts: number): string {
  const s = Math.round((Date.now() - ts) / 1000);
  if (s < 60)  return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
}

function fmtTime(ts: number): string {
  return new Date(ts).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function fmtDate(ts: number): string {
  return new Date(ts).toLocaleString('en-AU', { dateStyle: 'short', timeStyle: 'short' });
}

// Deterministic pseudo-random (seed-based so mock data is stable per render)
function rng(seed: number) {
  let s = seed;
  return () => { s = (s * 1664525 + 1013904223) & 0xffffffff; return (s >>> 0) / 4294967296; };
}

// ─── Mock data factories ──────────────────────────────────────────────────────

const CAMERAS = [
  'Main Entrance — North Gate',
  'Main Entrance — South Gate',
  'VIP Entrance — West',
  'Staff Gate — East',
  'Car Park Level 1',
  'Car Park Level 2',
  'Concourse A',
  'Concourse B',
  'Internal Corridor 1',
  'Emergency Exit 4',
];

const WATCHLIST_NAMES = [
  { name: 'Marcus Webb',    id: 'WL-1041', reason: 'Banned patron — assault 2024',    threat: 'high'   },
  { name: 'Sandra Okafor',  id: 'WL-0887', reason: 'Trespass order — expires Jun 2026', threat: 'medium' },
  { name: 'Darren Chu',     id: 'WL-1102', reason: 'Theft — ongoing investigation',   threat: 'high'   },
  { name: 'Lee Perkins',    id: 'WL-0345', reason: 'Alcohol ban — 6 months',          threat: 'low'    },
  { name: 'Amy Thornton',   id: 'WL-0991', reason: 'Violence — AVO in place',         threat: 'high'   },
  { name: 'Jordan Castillo', id: 'WL-0672', reason: 'Suspicious behaviour — flagged',  threat: 'medium' },
  { name: 'Raj Patel',      id: 'WL-1234', reason: 'Drug-related exclusion',          threat: 'medium' },
];

const ZONES = [
  { id: 'gate-a',    label: 'Gate A — North',     capacity: 2000, color: C.green },
  { id: 'gate-b',    label: 'Gate B — South',     capacity: 1800, color: C.blue  },
  { id: 'gate-c',    label: 'Gate C — West VIP',  capacity:  600, color: C.amber },
  { id: 'gate-d',    label: 'Gate D — Staff',     capacity:  400, color: C.purple },
  { id: 'concourse-a', label: 'Concourse A',      capacity: 5000, color: C.cyan  },
  { id: 'concourse-b', label: 'Concourse B',      capacity: 4500, color: C.green },
  { id: 'car-park-1',  label: 'Car Park L1',      capacity: 1200, color: C.blue  },
];

const ACCESS_NAMES = [
  'James Liu', 'Priya Sharma', 'Michael Torres', 'Emma Walsh',
  'David Kim', 'Sophie Nguyen', 'Liam O\'Brien', 'Zara Hassan',
  'Connor Reid', 'Yuki Tanaka', 'Ben Carter', 'Aaliyah Brooks',
];

let _alertSeq = 1000;
let _accessSeq = 500;

function genWatchlistAlert(r: () => number, now = Date.now()): WatchlistAlert {
  const person = WATCHLIST_NAMES[Math.floor(r() * WATCHLIST_NAMES.length)];
  const conf = Math.round(82 + r() * 16);
  return {
    id:         `ALT-${++_alertSeq}`,
    personId:   person.id,
    name:       person.name,
    reason:     person.reason,
    threat:     person.threat as any,
    confidence: conf,
    camera:     CAMERAS[Math.floor(r() * CAMERAS.length)],
    timestamp:  now - Math.floor(r() * 30000),
    status:     'new',
  };
}

function genAccessEvent(r: () => number, now = Date.now()): AccessEvent {
  const granted = r() > 0.12; // ~88% granted
  return {
    id:        `ACC-${++_accessSeq}`,
    name:      ACCESS_NAMES[Math.floor(r() * ACCESS_NAMES.length)],
    gate:      CAMERAS[Math.floor(r() * 5)], // only entry gates
    granted,
    reason:    granted ? undefined : (r() > 0.5 ? 'Not on guest list' : 'Access expired'),
    confidence: Math.round(85 + r() * 13),
    timestamp: now - Math.floor(r() * 120000),
  };
}

function genSeedAlerts(): WatchlistAlert[] {
  const r = rng(42);
  return Array.from({ length: 8 }, (_, i) =>
    genWatchlistAlert(r, Date.now() - i * 45000)
  );
}

function genSeedAccess(): AccessEvent[] {
  const r = rng(99);
  return Array.from({ length: 30 }, (_, i) =>
    genAccessEvent(r, Date.now() - i * 18000)
  );
}

function genZoneCounts(): ZoneCount[] {
  const r = rng(77);
  return ZONES.map(z => {
    const occupied = Math.round(z.capacity * (0.2 + r() * 0.7));
    return { ...z, occupied, trend: (r() > 0.5 ? 'up' : 'down') as any };
  });
}

function genFlowHistory(): FlowPoint[] {
  const r = rng(123);
  const now = Date.now();
  return Array.from({ length: 24 }, (_, i) => {
    const minsAgo = (23 - i) * 5; // 5-min buckets, last 2h
    const t = now - minsAgo * 60000;
    const base = 60 + Math.sin(i / 4) * 40;
    return {
      ts:      t,
      label:   new Date(t).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }),
      entries: Math.round(base + r() * 30),
      exits:   Math.round(base * 0.7 + r() * 20),
    };
  });
}

// Forensic mock: same person seen at multiple cameras
const FORENSIC_MATCHES = [
  { id: 'FM001', camera: 'Main Entrance — North Gate',  time: '08:22:14', confidence: 94, lat: 0.10, lon: 0.15 },
  { id: 'FM002', camera: 'Concourse A',                  time: '08:31:47', confidence: 89, lat: 0.35, lon: 0.40 },
  { id: 'FM003', camera: 'Car Park Level 1',              time: '08:44:02', confidence: 91, lat: 0.60, lon: 0.20 },
  { id: 'FM004', camera: 'Internal Corridor 1',          time: '09:02:18', confidence: 87, lat: 0.70, lon: 0.55 },
  { id: 'FM005', camera: 'Concourse B',                  time: '09:15:33', confidence: 92, lat: 0.45, lon: 0.70 },
  { id: 'FM006', camera: 'Emergency Exit 4',             time: '09:28:55', confidence: 83, lat: 0.80, lon: 0.80 },
];

// ─── Types ────────────────────────────────────────────────────────────────────

interface WatchlistAlert {
  id: string;
  personId: string;
  name: string;
  reason: string;
  threat: 'low' | 'medium' | 'high';
  confidence: number;
  camera: string;
  timestamp: number;
  status: 'new' | 'acknowledged' | 'dismissed';
}

interface AccessEvent {
  id: string;
  name: string;
  gate: string;
  granted: boolean;
  reason?: string;
  confidence: number;
  timestamp: number;
}

interface ZoneCount {
  id: string;
  label: string;
  capacity: number;
  occupied: number;
  color: string;
  trend: 'up' | 'down' | 'stable';
}

interface FlowPoint {
  ts: number;
  label: string;
  entries: number;
  exits: number;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function Card({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{
      background: C.surface,
      border: `1px solid ${C.border}`,
      borderRadius: 12,
      ...style,
    }}>
      {children}
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 700, color: C.textDim, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 12 }}>
      {children}
    </div>
  );
}

function Badge({ label, color, bg }: { label: string; color: string; bg: string }) {
  return (
    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', padding: '2px 8px', borderRadius: 4, color, background: bg }}>
      {label}
    </span>
  );
}

// Placeholder face thumbnail — will show real snapshots when Corsight is wired in
function FaceThumbnail({ size = 48, initials }: { size?: number; initials: string }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: 8, flexShrink: 0,
      background: 'rgba(59,130,246,0.15)', border: `1px solid rgba(59,130,246,0.3)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.35, color: C.blue, fontWeight: 700, letterSpacing: -0.5,
    }}>
      {initials}
    </div>
  );
}

// ─── TAB 1 — Watchlist Alerts ────────────────────────────────────────────────

const THREAT_META = {
  high:   { color: C.red,    bg: C.redDim,    label: 'HIGH THREAT'   },
  medium: { color: C.amber,  bg: C.amberDim,  label: 'MEDIUM'        },
  low:    { color: C.green,  bg: C.greenDim,  label: 'LOW'           },
};

function WatchlistTab() {
  const { isDemoOrg } = useLicense();
  // CORSIGHT API: GET /api/watchlist/alerts?limit=50&since=<timestamp>
  // Replace the useState initial value and the polling block below with real fetch calls.
  const [alerts, setAlerts] = useState<WatchlistAlert[]>([]);
  const [filter, setFilter] = useState<'all' | 'new' | 'acknowledged' | 'dismissed'>('all');
  const [camFilter, setCamFilter] = useState('All');
  const [flash, setFlash] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setInitialLoading(false), 700);
    return () => clearTimeout(t);
  }, []);

  // Re-sync when org switches
  useEffect(() => {
    setAlerts([]);
  }, [isDemoOrg]); // eslint-disable-line react-hooks/exhaustive-deps

  // CORSIGHT API: real alerts will arrive via WebSocket/SSE when Corsight is configured.

  const ack   = useCallback((id: string) => setAlerts(p => p.map(a => a.id === id ? { ...a, status: 'acknowledged' } : a)), []);
  const dism  = useCallback((id: string) => setAlerts(p => p.map(a => a.id === id ? { ...a, status: 'dismissed'   } : a)), []);

  const cameras = ['All', ...Array.from(new Set(alerts.map(a => a.camera)))];

  const visible = alerts.filter(a =>
    (filter === 'all' || a.status === filter) &&
    (camFilter === 'All' || a.camera === camFilter)
  );

  const newCount = alerts.filter(a => a.status === 'new').length;

  return (
    <div>
      {/* Stats strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { l: 'New Alerts',     v: alerts.filter(a=>a.status==='new').length,          c: C.red    },
          { l: 'Acknowledged',   v: alerts.filter(a=>a.status==='acknowledged').length, c: C.amber  },
          { l: 'Dismissed',      v: alerts.filter(a=>a.status==='dismissed').length,    c: C.textDim },
          { l: 'Total Today',    v: alerts.length,                                      c: C.text   },
        ].map(s => (
          <Card key={s.l} style={{ padding: '14px 16px' }}>
            <div style={{ fontSize: 10, color: C.textDim, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 }}>{s.l}</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: s.c }}>{s.v}</div>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 4, background: '#111111', borderRadius: 8, padding: 4 }}>
          {(['all','new','acknowledged','dismissed'] as const).map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{ padding: '5px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, background: filter===f ? C.blue : 'transparent', color: filter===f ? '#fff' : C.textMid }}>
              {f.charAt(0).toUpperCase()+f.slice(1)}{f==='new' && newCount > 0 ? ` (${newCount})` : ''}
            </button>
          ))}
        </div>
        <select value={camFilter} onChange={e => setCamFilter(e.target.value)} style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, borderRadius: 8, padding: '6px 12px', color: C.text, fontSize: 12 }}>
          {cameras.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: C.textDim }}>{visible.length} alert{visible.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Alert list */}
      {initialLoading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 10 }}>
          {Array.from({length:5}).map((_,i) => (
            <div key={i} style={{ display:'flex', gap:14, padding:'14px 16px', borderRadius:10, background:'#0d0d0d', border:`1px solid ${C.border}`, borderLeft:`4px solid rgba(255,255,255,0.06)` }}>
              <div className="skeleton" style={{ width:52, height:52, borderRadius:'50%', flexShrink:0 }} />
              <div style={{ flex:1 }}>
                <div className="skeleton" style={{ height:13, width:'40%', marginBottom:6 }} />
                <div className="skeleton" style={{ height:11, width:'60%', marginBottom:8 }} />
                <div style={{ display:'flex', gap:8 }}>
                  <div className="skeleton" style={{ height:20, width:60, borderRadius:10 }} />
                  <div className="skeleton" style={{ height:20, width:80, borderRadius:10 }} />
                </div>
              </div>
              <div style={{ display:'flex', gap:6 }}>
                <div className="skeleton" style={{ height:30, width:80, borderRadius:8 }} />
                <div className="skeleton" style={{ height:30, width:70, borderRadius:8 }} />
              </div>
            </div>
          ))}
        </div>
      )}
      {!initialLoading && visible.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: C.textFaint }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>🔍</div>
          <div style={{ fontSize: 15 }}>No alerts match the current filter</div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {!initialLoading && visible.map(a => {
          const meta = THREAT_META[a.threat];
          const isNew = a.status === 'new';
          const isFlashing = flash === a.id;
          return (
            <div key={a.id} style={{
              display: 'flex', alignItems: 'flex-start', gap: 14,
              padding: '14px 16px', borderRadius: 10,
              background: isFlashing ? 'rgba(239,68,68,0.08)' : isNew ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.01)',
              border: `1px solid ${isFlashing ? 'rgba(239,68,68,0.4)' : isNew ? C.border : 'rgba(255,255,255,0.04)'}`,
              borderLeft: `4px solid ${meta.color}`,
              opacity: a.status === 'dismissed' ? 0.45 : 1,
              transition: 'all 0.3s',
            }}>
              {/* Face thumbnail placeholder */}
              {/* CORSIGHT API: replace FaceThumbnail with <img src={corsightSnapshotUrl(a.personId)} .../> */}
              <FaceThumbnail size={52} initials={a.name.split(' ').map(n=>n[0]).join('')}/>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{a.name}</span>
                  <Badge label={meta.label} color={meta.color} bg={meta.bg}/>
                  {isNew && <Badge label="NEW" color="#fff" bg={C.redDim}/>}
                  <span style={{ fontSize: 11, color: meta.color, fontWeight: 700 }}>{a.confidence}% match</span>
                </div>
                <div style={{ fontSize: 12, color: C.textMid, marginBottom: 2 }}>
                  <span style={{ color: C.textDim }}>{a.personId}</span> — {a.reason}
                </div>
                <div style={{ fontSize: 11, color: C.textDim }}>
                  📷 {a.camera} · {timeAgo(a.timestamp)}
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end', flexShrink: 0 }}>
                <span style={{ fontSize: 11, color: C.textDim, fontFamily: 'monospace' }}>{fmtTime(a.timestamp)}</span>
                {isNew && (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => ack(a.id)} style={{ padding: '5px 12px', borderRadius: 6, border: 'none', background: C.amberDim, color: C.amber, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Acknowledge</button>
                    <button onClick={() => dism(a.id)} style={{ padding: '5px 10px', borderRadius: 6, border: `1px solid ${C.border}`, background: 'none', color: C.textDim, fontSize: 11, cursor: 'pointer' }}>Dismiss</button>
                  </div>
                )}
                {a.status === 'acknowledged' && <Badge label="Acknowledged" color={C.amber} bg={C.amberDim}/>}
                {a.status === 'dismissed'   && <Badge label="Dismissed"    color={C.textDim} bg="rgba(255,255,255,0.04)"/>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── TAB 2 — People Counting & Crowd Analytics ───────────────────────────────

function PeopleCountingTab() {
  // CORSIGHT API: GET /api/analytics/zones — replace with real endpoint
  const [zones, setZones] = useState<ZoneCount[]>(genZoneCounts);
  const [flow, setFlow] = useState<FlowPoint[]>(genFlowHistory);
  const [totalUnique] = useState(() => 8400 + Math.floor(Math.random() * 1200));
  const [tick, setTick] = useState(0);

  // Mock update every 10 seconds
  // CORSIGHT API: replace interval with real polling or WebSocket subscription
  useEffect(() => {
    const t = setInterval(() => {
      setZones(prev => prev.map(z => {
        const delta = Math.floor(Math.random() * 20) - 8;
        return { ...z, occupied: Math.max(0, Math.min(z.capacity, z.occupied + delta)), trend: delta > 0 ? 'up' : delta < 0 ? 'down' : 'stable' };
      }));
      setFlow(prev => {
        const r = rng(Date.now() % 99999);
        const newPt: FlowPoint = {
          ts: Date.now(), label: new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }),
          entries: Math.round(80 + r() * 40), exits: Math.round(60 + r() * 30),
        };
        return [...prev.slice(1), newPt];
      });
      setTick(t => t + 1);
    }, 10000);
    return () => clearInterval(t);
  }, []);

  const totalOcc = zones.reduce((s, z) => s + z.occupied, 0);
  const totalCap = zones.reduce((s, z) => s + z.capacity, 0);
  const totalPct = Math.round((totalOcc / totalCap) * 100);
  const capacityColor = totalPct > 90 ? C.red : totalPct > 75 ? C.amber : C.green;

  // SVG flow line chart
  const chartW = 500; const chartH = 80; const padX = 10; const padY = 8;
  const maxVal = Math.max(...flow.map(p => Math.max(p.entries, p.exits)), 1);
  function toX(i: number) { return padX + (i / (flow.length - 1)) * (chartW - padX * 2); }
  function toY(v: number) { return chartH - padY - (v / maxVal) * (chartH - padY * 2); }
  const entryPath = flow.map((p, i) => `${i === 0 ? 'M' : 'L'}${toX(i)},${toY(p.entries)}`).join(' ');
  const exitPath  = flow.map((p, i) => `${i === 0 ? 'M' : 'L'}${toX(i)},${toY(p.exits)}`).join(' ');
  const entryFill = `${entryPath} L${toX(flow.length-1)},${chartH-padY} L${toX(0)},${chartH-padY} Z`;
  const exitFill  = `${exitPath}  L${toX(flow.length-1)},${chartH-padY} L${toX(0)},${chartH-padY} Z`;

  // Heatmap grid (8×12 cells, coloured by zone crowd level — simplified floor plan)
  const heatCells = useMemo(() => {
    const r = rng(tick * 7 + 111);
    return Array.from({ length: 96 }, (_, i) => {
      const base = r();
      // Hotspot regions
      const row = Math.floor(i / 12); const col = i % 12;
      let val = base * 0.2;
      [[1,2],[3,8],[5,5],[2,10]].forEach(([hr,hc]) => {
        const d = Math.sqrt((row-hr)**2 + (col-hc)**2);
        if (d < 3) val += (1 - d/3) * 0.8;
      });
      return Math.min(1, val);
    });
  }, [tick]);

  function heatColor(v: number) {
    if (v < 0.2)  return `rgba(34,197,94,${0.15+v*1.5})`;
    if (v < 0.5)  return `rgba(245,158,11,${0.3+v*0.8})`;
    if (v < 0.75) return `rgba(239,68,68,${0.5+v*0.6})`;
    return `rgba(220,38,38,${0.7+v*0.3})`;
  }

  return (
    <div>
      {/* Top KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { l: 'Total Occupancy',    v: totalOcc.toLocaleString(),     c: capacityColor  },
          { l: 'Total Capacity',     v: totalCap.toLocaleString(),     c: C.text         },
          { l: 'Utilisation',        v: `${totalPct}%`,                c: capacityColor  },
          { l: 'Unique Visitors Today', v: totalUnique.toLocaleString(), c: C.cyan       },
        ].map(s => (
          <Card key={s.l} style={{ padding: '14px 16px' }}>
            <div style={{ fontSize: 10, color: C.textDim, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 }}>{s.l}</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: s.c }}>{s.v}</div>
          </Card>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        {/* Zone capacity bars */}
        <Card style={{ padding: 16 }}>
          <SectionTitle>Zone Occupancy</SectionTitle>
          {zones.map(z => {
            const pct = Math.round((z.occupied / z.capacity) * 100);
            const col = pct > 90 ? C.red : pct > 75 ? C.amber : z.color;
            return (
              <div key={z.id} style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                  <span style={{ color: C.textMid }}>{z.label}</span>
                  <span style={{ color: col, fontWeight: 700 }}>
                    {z.occupied.toLocaleString()} / {z.capacity.toLocaleString()}
                    <span style={{ color: C.textDim, fontWeight: 400, marginLeft: 6 }}>({pct}%)</span>
                    <span style={{ marginLeft: 6 }}>{z.trend === 'up' ? '↑' : z.trend === 'down' ? '↓' : '—'}</span>
                  </span>
                </div>
                <div style={{ height: 8, background: 'rgba(255,255,255,0.06)', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${pct}%`, background: col, borderRadius: 4, transition: 'width 0.6s ease' }}/>
                </div>
              </div>
            );
          })}
        </Card>

        {/* Crowd flow line chart */}
        <Card style={{ padding: 16 }}>
          <SectionTitle>Crowd Flow — Last 2 Hours</SectionTitle>
          <svg viewBox={`0 0 ${chartW} ${chartH}`} style={{ width: '100%', height: 90 }}>
            <defs>
              <linearGradient id="entryGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={C.green} stopOpacity="0.3"/>
                <stop offset="100%" stopColor={C.green} stopOpacity="0"/>
              </linearGradient>
              <linearGradient id="exitGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={C.red} stopOpacity="0.3"/>
                <stop offset="100%" stopColor={C.red} stopOpacity="0"/>
              </linearGradient>
            </defs>
            <path d={entryFill} fill="url(#entryGrad)"/>
            <path d={exitFill}  fill="url(#exitGrad)"/>
            <path d={entryPath} stroke={C.green} strokeWidth="1.5" fill="none"/>
            <path d={exitPath}  stroke={C.red}   strokeWidth="1.5" fill="none"/>
            {flow.filter((_,i) => i % 4 === 0).map((p, i) => (
              <text key={i} x={toX(i*4)} y={chartH-1} textAnchor="middle" fontSize="7" fill={C.textDim}>{p.label}</text>
            ))}
          </svg>
          <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 11 }}>
            <span style={{ color: C.green }}>▬ Entries</span>
            <span style={{ color: C.red   }}>▬ Exits</span>
          </div>
          <div style={{ display: 'flex', gap: 16, marginTop: 6, fontSize: 12 }}>
            <span style={{ color: C.textDim }}>Current: <span style={{ color: C.green, fontWeight: 700 }}>{flow[flow.length-1]?.entries} entry/min</span></span>
            <span style={{ color: C.textDim }}>         <span style={{ color: C.red,   fontWeight: 700 }}>{flow[flow.length-1]?.exits} exit/min</span></span>
          </div>
        </Card>
      </div>

      {/* Heatmap overview */}
      <Card style={{ padding: 16 }}>
        <SectionTitle>Crowd Density Heatmap — Venue Overview</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12,1fr)', gap: 2, borderRadius: 6, overflow: 'hidden', marginBottom: 8 }}>
          {heatCells.map((v, i) => (
            <div key={i} title={`Density: ${Math.round(v*100)}%`} style={{ height: 24, background: heatColor(v), borderRadius: 2, transition: 'background 0.5s' }}/>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'flex-end', fontSize: 10, color: C.textDim }}>
          {[[0,'Low'],[0.4,''],[0.7,''],[1,'High']].map(([v,l],i) => (
            <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 12, height: 12, borderRadius: 2, background: heatColor(v as number), display: 'inline-block' }}/>
              {l}
            </span>
          ))}
        </div>
        <div style={{ fontSize: 11, color: C.textDim, marginTop: 8 }}>
          Updates every 10 seconds · Auto-refresh active
          {/* CORSIGHT API: replace heatmap with real occupancy grid from analytics API */}
        </div>
      </Card>
    </div>
  );
}

// ─── TAB 3 — Access Control ───────────────────────────────────────────────────

function AccessControlTab() {
  // CORSIGHT API: GET /api/access/events?limit=100&since=<timestamp>
  // Replace mock data and polling with real endpoint subscription
  const [events, setEvents] = useState<AccessEvent[]>([]);
  const [search, setSearch] = useState('');
  const [gateFilter, setGateFilter] = useState('All');

  // CORSIGHT API: real access events will arrive via WebSocket/SSE when Corsight is configured.

  const gates = ['All', ...Array.from(new Set(events.map(e => e.gate)))];

  const visible = events.filter(e =>
    (gateFilter === 'All' || e.gate === gateFilter) &&
    (search === '' || e.name.toLowerCase().includes(search.toLowerCase()) || e.gate.toLowerCase().includes(search.toLowerCase()))
  );

  const granted = events.filter(e => e.granted).length;
  const denied  = events.filter(e => !e.granted).length;

  return (
    <div>
      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { l: 'Total Entries Today', v: events.length,  c: C.text  },
          { l: 'Access Granted',      v: granted,         c: C.green },
          { l: 'Access Denied',       v: denied,          c: C.red   },
        ].map(s => (
          <Card key={s.l} style={{ padding: '14px 16px' }}>
            <div style={{ fontSize: 10, color: C.textDim, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 }}>{s.l}</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: s.c }}>{s.v}</div>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by name or gate…"
          style={{ flex: 1, minWidth: 200, background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, borderRadius: 8, padding: '7px 12px', color: C.text, fontSize: 12 }}
        />
        <select value={gateFilter} onChange={e => setGateFilter(e.target.value)} style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, borderRadius: 8, padding: '7px 12px', color: C.text, fontSize: 12 }}>
          {gates.map(g => <option key={g} value={g}>{g}</option>)}
        </select>
      </div>

      {/* Event log table */}
      <Card>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                {['', 'Name', 'Gate', 'Result', 'Confidence', 'Time'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: C.textDim, textTransform: 'uppercase', letterSpacing: 0.8 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.slice(0, 40).map((e, i) => (
                <tr key={e.id} style={{ borderBottom: `1px solid rgba(255,255,255,0.04)`, background: i === 0 ? 'rgba(59,130,246,0.04)' : 'transparent' }}>
                  <td style={{ padding: '8px 14px' }}>
                    {/* CORSIGHT API: replace with real snapshot thumbnail */}
                    <FaceThumbnail size={32} initials={e.name.split(' ').map(n=>n[0]).join('')}/>
                  </td>
                  <td style={{ padding: '8px 14px', color: C.text, fontWeight: 600 }}>{e.name}</td>
                  <td style={{ padding: '8px 14px', color: C.textMid }}>{e.gate}</td>
                  <td style={{ padding: '8px 14px' }}>
                    {e.granted
                      ? <Badge label="Granted" color={C.green} bg={C.greenDim}/>
                      : <span><Badge label="Denied" color={C.red} bg={C.redDim}/> <span style={{ fontSize: 11, color: C.textDim, marginLeft: 6 }}>{e.reason}</span></span>
                    }
                  </td>
                  <td style={{ padding: '8px 14px', color: C.textMid }}>{e.confidence}%</td>
                  <td style={{ padding: '8px 14px', color: C.textDim, fontFamily: 'monospace', fontSize: 11 }}>{fmtDate(e.timestamp)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {visible.length === 0 && (
          <div style={{ padding: '40px', textAlign: 'center', color: C.textFaint }}>No entries match your search</div>
        )}
      </Card>
    </div>
  );
}

// ─── TAB 4 — Forensic Search ─────────────────────────────────────────────────

type SearchState = 'idle' | 'loading' | 'results' | 'no-results';

function ForensicSearchTab() {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [searchState, setSearchState] = useState<SearchState>('idle');
  const [matches, setMatches] = useState(FORENSIC_MATCHES);
  const dropRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  function handleFile(f: File) {
    setFile(f);
    setSearchState('idle');
    const reader = new FileReader();
    reader.onload = e => setPreview(e.target?.result as string);
    reader.readAsDataURL(f);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f && f.type.startsWith('image/')) handleFile(f);
  }

  function runSearch() {
    if (!file) return;
    setSearchState('loading');
    // CORSIGHT API: POST /api/forensic/search — multipart form with image file
    // Expected response: { matches: [{ camera, timestamp, confidence, snapshot_url, track_id }] }
    // Replace this setTimeout block with a real fetch call:
    //   const form = new FormData(); form.append('image', file);
    //   const res = await fetch('/api/lemai_face/forensic/search', { method: 'POST', body: form });
    //   const data = await res.json();
    //   setMatches(data.matches);
    setTimeout(() => {
      // Randomise mock matches slightly each search
      setMatches(FORENSIC_MATCHES.map(m => ({ ...m, confidence: Math.round(80 + Math.random() * 18) })));
      setSearchState('results');
    }, 2000);
  }

  function reset() {
    setFile(null);
    setPreview(null);
    setSearchState('idle');
  }

  // Timeline bar widths
  const timelineStart = matches.length > 0 ? 8 * 60 + 0  : 0;   // 08:00
  const timelineEnd   = matches.length > 0 ? 9  * 60 + 40 : 100; // 09:40
  const timelineRange = timelineEnd - timelineStart || 1;

  function parseMinutes(t: string) {
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
  }

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 20 }}>
        {/* Left panel — upload */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Card style={{ padding: 16 }}>
            <SectionTitle>Upload Face Image</SectionTitle>

            {/* Drop zone */}
            <div
              ref={dropRef}
              onClick={() => fileRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={e => e.preventDefault()}
              style={{
                border: `2px dashed ${preview ? C.blue : C.border}`,
                borderRadius: 10, padding: '24px 16px', textAlign: 'center',
                cursor: 'pointer', marginBottom: 14, transition: 'border-color 0.2s',
                background: preview ? 'rgba(59,130,246,0.05)' : 'rgba(255,255,255,0.02)',
                minHeight: 180, display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 10,
              }}
            >
              {preview ? (
                <img src={preview} alt="Upload preview" style={{ maxWidth: '100%', maxHeight: 160, borderRadius: 6, objectFit: 'contain' }}/>
              ) : (
                <>
                  <div style={{ fontSize: 36 }}>🖼️</div>
                  <div style={{ fontSize: 13, color: C.textMid }}>Drop photo here or click to browse</div>
                  <div style={{ fontSize: 11, color: C.textDim }}>JPG, PNG · Face should be clearly visible</div>
                </>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])} style={{ display: 'none' }}/>

            {file && (
              <div style={{ fontSize: 11, color: C.textDim, marginBottom: 12, wordBreak: 'break-all' }}>
                📎 {file.name} ({Math.round(file.size / 1024)} KB)
              </div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={runSearch}
                disabled={!file || searchState === 'loading'}
                style={{ flex: 1, padding: '10px', borderRadius: 8, border: 'none', background: file && searchState !== 'loading' ? C.blue : 'rgba(255,255,255,0.06)', color: file && searchState !== 'loading' ? '#fff' : C.textDim, fontSize: 13, fontWeight: 700, cursor: file && searchState !== 'loading' ? 'pointer' : 'default', transition: 'all 0.2s' }}
              >
                {searchState === 'loading' ? '🔍 Searching…' : '🔍 Search Venue'}
              </button>
              {file && (
                <button onClick={reset} style={{ padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.border}`, background: 'none', color: C.textDim, fontSize: 12, cursor: 'pointer' }}>
                  Reset
                </button>
              )}
            </div>

            {searchState === 'loading' && (
              <div style={{ marginTop: 12, padding: '10px 14px', background: C.blueDim, borderRadius: 8, fontSize: 12, color: C.blue }}>
                ⏳ Scanning venue footage…
                {/* CORSIGHT API: this loading state is shown while waiting for /api/forensic/search response */}
              </div>
            )}
          </Card>

          {/* Search tips */}
          <Card style={{ padding: 14 }}>
            <SectionTitle>Search Tips</SectionTitle>
            {[
              'Use a clear frontal face photo for best accuracy',
              'CCTV stills work well — resolution ≥ 200×200px',
              'Multiple matches indicate same person across cameras',
              'Timeline shows the person\'s movement path through the venue',
            ].map((tip, i) => (
              <div key={i} style={{ fontSize: 12, color: C.textDim, marginBottom: 6, display: 'flex', gap: 8 }}>
                <span style={{ color: C.blue, flexShrink: 0 }}>•</span> {tip}
              </div>
            ))}
          </Card>
        </div>

        {/* Right panel — results */}
        <div>
          {searchState === 'idle' && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 300, color: C.textFaint, flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: 40 }}>🔎</div>
              <div style={{ fontSize: 15 }}>Upload a face image to search venue footage</div>
            </div>
          )}

          {searchState === 'results' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>
                  {matches.length} match{matches.length !== 1 ? 'es' : ''} found
                </div>
                <Badge label="MOCK DATA — Corsight API pending" color={C.amber} bg={C.amberDim}/>
              </div>

              {/* Match cards */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
                {matches.map((m, i) => (
                  <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 6, background: `rgba(59,130,246,${0.1+i*0.05})`, border: `1px solid rgba(59,130,246,0.3)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: C.blue, fontWeight: 700, flexShrink: 0 }}>
                      {i+1}
                    </div>
                    {/* CORSIGHT API: replace placeholder with <img src={corsightSnapshotUrl(m.track_id)} .../> */}
                    <FaceThumbnail size={44} initials="?"/>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{m.camera}</div>
                      <div style={{ fontSize: 11, color: C.textDim, marginTop: 2 }}>🕐 {m.time}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 16, fontWeight: 700, color: m.confidence >= 90 ? C.green : C.amber }}>{m.confidence}%</div>
                      <div style={{ fontSize: 10, color: C.textDim }}>match</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Movement timeline */}
              <Card style={{ padding: 16 }}>
                <SectionTitle>Movement Timeline</SectionTitle>
                <div style={{ position: 'relative', marginBottom: 8 }}>
                  {/* Time axis */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: C.textDim, marginBottom: 8 }}>
                    <span>08:00</span><span>08:30</span><span>09:00</span><span>09:30</span>
                  </div>
                  {/* Track line */}
                  <div style={{ position: 'relative', height: 6, background: 'rgba(255,255,255,0.05)', borderRadius: 3, marginBottom: 24 }}>
                    <div style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, background: `linear-gradient(to right, ${C.blue}, ${C.cyan})`, borderRadius: 3, opacity: 0.3 }}/>
                    {matches.map((m, i) => {
                      const mins = parseMinutes(m.time.slice(0,5));
                      const leftPct = ((mins - timelineStart) / timelineRange) * 100;
                      return (
                        <div key={m.id} title={`${m.camera} — ${m.time}`} style={{ position: 'absolute', top: '50%', left: `${leftPct}%`, transform: 'translate(-50%,-50%)', width: 14, height: 14, borderRadius: '50%', background: m.confidence >= 90 ? C.green : C.amber, border: '2px solid #0a0f1e', boxShadow: `0 0 6px ${m.confidence >= 90 ? C.green : C.amber}`, cursor: 'pointer', zIndex: 2 }}/>
                      );
                    })}
                  </div>
                  {/* Camera labels */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {matches.map((m, i) => {
                      const mins = parseMinutes(m.time.slice(0,5));
                      const leftPct = ((mins - timelineStart) / timelineRange) * 100;
                      return (
                        <div key={m.id} style={{ display: 'flex', gap: 10, fontSize: 11, alignItems: 'center' }}>
                          <div style={{ width: 8, height: 8, borderRadius: '50%', background: m.confidence >= 90 ? C.green : C.amber, flexShrink: 0 }}/>
                          <span style={{ color: C.textDim, width: 50, flexShrink: 0 }}>{m.time}</span>
                          <span style={{ color: C.textMid }}>{m.camera}</span>
                          <span style={{ marginLeft: 'auto', color: m.confidence >= 90 ? C.green : C.amber }}>{m.confidence}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div style={{ fontSize: 11, color: C.textDim, marginTop: 12, borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
                  {/* CORSIGHT API: timeline data comes from /api/forensic/tracks/<track_id>/timeline */}
                  💡 Timeline shows detected locations in chronological order. Dots indicate camera matches.
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const TABS = [
  { id: 'watchlist',   label: 'Watchlist Alerts',  icon: '🚨' },
  { id: 'counting',    label: 'People Counting',    icon: '👥' },
  { id: 'access',      label: 'Access Control',     icon: '🚪' },
  { id: 'forensic',    label: 'Forensic Search',    icon: '🔍' },
] as const;

type TabId = typeof TABS[number]['id'];

export default function LemaiFC() {
  const [activeTab, setActiveTab] = useState<TabId>('watchlist');

  return (
    <Layout title="Lemai Face" subtitle="Facial recognition — watchlist alerts, people counting, access control & forensic search">
      {/* Integration pending banner */}
      <div style={{ backgroundColor:'#422006', border:'1px solid #f59e0b', padding:'8px 16px', marginBottom:'14px', display:'flex', alignItems:'center', gap:'8px' }}>
        <span style={{ fontSize:'10px', color:'#f59e0b' }}>⚠️  LEMAI FACE — INTEGRATION PENDING — This module requires Corsight API configuration. Contact your administrator to enable live facial recognition.</span>
      </div>
      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, background: '#0d0d0d', border: `1px solid ${C.border}`, borderRadius: 10, padding: 5, flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            style={{
              flex: '1 1 auto', padding: '9px 16px', borderRadius: 7, border: 'none', cursor: 'pointer',
              background: activeTab === t.id ? C.blue : 'transparent',
              color: activeTab === t.id ? '#fff' : C.textMid,
              fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center',
              justifyContent: 'center', gap: 7, transition: 'all 0.2s',
            }}
          >
            <span>{t.icon}</span> {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'watchlist' && <WatchlistTab/>}
      {activeTab === 'counting'  && <PeopleCountingTab/>}
      {activeTab === 'access'    && <AccessControlTab/>}
      {activeTab === 'forensic'  && <ForensicSearchTab/>}
    </Layout>
  );
}
